import React, { useState, useEffect, useRef } from 'react';
import { announcementsAPI, adminAPI, emailListsAPI, API_BASE_URL } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import BrandVisibilityControl, { deriveVisibilityMode } from './BrandVisibilityControl';

const AnnouncementsEditor = () => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [emailLists, setEmailLists] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general',
    is_pinned: false,
    document_url: null,
    document_name: null,
    visibility_mode: 'all',
    for_all_brands: true,
    target_domains: [],
    email_list_ids: [],
  });
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchAnnouncements();
    fetchBrands();
    fetchEmailLists();
  }, []);

  const fetchEmailLists = async () => {
    try {
      const data = await emailListsAPI.getLists();
      setEmailLists(data.filter(l => l.is_active));
    } catch (err) {
      console.error('Error fetching email lists:', err);
    }
  };

  const handleEmailListToggle = (listId) => {
    setFormData(prev => {
      const newIds = prev.email_list_ids.includes(listId)
        ? prev.email_list_ids.filter(id => id !== listId)
        : [...prev.email_list_ids, listId];
      return { ...prev, email_list_ids: newIds };
    });
  };

  const fetchBrands = async () => {
    try {
      const data = await adminAPI.getAllowedDomains();
      const brands = data
        .filter(d => d.is_active && d.domain && !d.domain.includes('drummond'))
        .map(d => d.domain);
      setAvailableBrands(brands);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const handleBrandToggle = (domain) => {
    setFormData(prev => {
      const newDomains = prev.target_domains.includes(domain)
        ? prev.target_domains.filter(d => d !== domain)
        : [...prev.target_domains, domain];
      return { ...prev, target_domains: newDomains };
    });
  };

  // Handle paste event for images
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          await uploadAndInsertImage(file);
        }
        break;
      }
    }
  };

  // Upload image and insert markdown reference
  const uploadAndInsertImage = async (file) => {
    setIsUploading(true);
    try {
      const result = await announcementsAPI.uploadImage(file);
      const imageMarkdown = `![image](${result.image_url})`;

      // Insert at cursor position or append
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentContent = formData.content;
        const newContent =
          currentContent.substring(0, start) +
          imageMarkdown +
          currentContent.substring(end);
        setFormData({ ...formData, content: newContent });

        // Set cursor position after the inserted text
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd =
            start + imageMarkdown.length;
          textarea.focus();
        }, 0);
      } else {
        setFormData({
          ...formData,
          content: formData.content + '\n' + imageMarkdown,
        });
      }

      setMessage({ type: 'success', text: 'Image uploaded successfully' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to upload image',
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file input for manual image upload
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadAndInsertImage(file);
      e.target.value = ''; // Reset input
    }
  };

  // Handle document upload
  const handleDocumentSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploadingDocument(true);
      try {
        const result = await announcementsAPI.uploadDocument(file);
        setFormData({
          ...formData,
          document_url: result.document_url,
          document_name: result.document_name,
        });
        setMessage({ type: 'success', text: 'Document uploaded successfully' });
      } catch (err) {
        setMessage({
          type: 'error',
          text: err.response?.data?.detail || 'Failed to upload document',
        });
      } finally {
        setIsUploadingDocument(false);
        e.target.value = ''; // Reset input
      }
    }
  };

  // Remove attached document
  const handleRemoveDocument = () => {
    setFormData({
      ...formData,
      document_url: null,
      document_name: null,
    });
  };

  // Handle video upload and insert into content
  const handleVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploadingVideo(true);
      try {
        const result = await announcementsAPI.uploadVideo(file);
        const videoMarkdown = `[video](${result.video_url})`;

        // Insert at cursor position or append
        const textarea = textareaRef.current;
        if (textarea) {
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const currentContent = formData.content;
          const newContent =
            currentContent.substring(0, start) +
            videoMarkdown +
            currentContent.substring(end);
          setFormData({ ...formData, content: newContent });

          // Set cursor position after the inserted text
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd =
              start + videoMarkdown.length;
            textarea.focus();
          }, 0);
        } else {
          setFormData({
            ...formData,
            content: formData.content + '\n' + videoMarkdown,
          });
        }

        setMessage({ type: 'success', text: 'Video uploaded successfully' });
      } catch (err) {
        setMessage({
          type: 'error',
          text: err.response?.data?.detail || 'Failed to upload video',
        });
      } finally {
        setIsUploadingVideo(false);
        e.target.value = ''; // Reset input
      }
    }
  };

  const fetchAnnouncements = async () => {
    setIsLoading(true);
    try {
      const data = await announcementsAPI.getAnnouncements(false, 50);
      setAnnouncements(data);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setMessage({ type: 'error', text: 'Failed to load announcements' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Derive send_email from whether email lists are selected
    const submitData = {
      ...formData,
      send_email: formData.email_list_ids.length > 0,
    };

    try {
      if (editingId) {
        await announcementsAPI.updateAnnouncement(editingId, submitData);
        setMessage({ type: 'success', text: 'Announcement updated successfully' });
      } else {
        await announcementsAPI.createAnnouncement(submitData);
        setMessage({ type: 'success', text: 'Announcement created successfully' });
      }
      resetForm();
      fetchAnnouncements();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to save announcement',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      title: announcement.title,
      content: announcement.content,
      category: announcement.category,
      is_pinned: announcement.is_pinned,
      document_url: announcement.document_url,
      document_name: announcement.document_name,
      visibility_mode: deriveVisibilityMode(announcement),
      for_all_brands: announcement.for_all_brands ?? true,
      target_domains: announcement.target_domains || [],
      email_list_ids: announcement.email_list_ids || [],
    });
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleToggleActive = async (announcement) => {
    try {
      await announcementsAPI.updateAnnouncement(announcement.id, {
        is_active: !announcement.is_active,
      });
      setMessage({
        type: 'success',
        text: `Announcement ${announcement.is_active ? 'hidden' : 'shown'}`,
      });
      fetchAnnouncements();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to update announcement',
      });
    }
  };

  const handleTogglePinned = async (announcement) => {
    try {
      await announcementsAPI.updateAnnouncement(announcement.id, {
        is_pinned: !announcement.is_pinned,
      });
      setMessage({
        type: 'success',
        text: `Announcement ${announcement.is_pinned ? 'unpinned' : 'pinned'}`,
      });
      fetchAnnouncements();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to update announcement',
      });
    }
  };

  const handleDelete = async (announcement) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${announcement.title}"? This action cannot be undone.`
      )
    )
      return;

    try {
      await announcementsAPI.deleteAnnouncement(announcement.id);
      setMessage({ type: 'success', text: 'Announcement deleted successfully' });
      fetchAnnouncements();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to delete announcement',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: 'general',
      is_pinned: false,
      document_url: null,
      document_name: null,
      visibility_mode: 'all',
      for_all_brands: true,
      target_domains: [],
      email_list_ids: [],
    });
    setEditingId(null);
    setShowForm(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const getCategoryBadge = (category) => {
    const categoryStyles = {
      general: 'bg-secondary',
      update: 'bg-info',
      alert: 'bg-warning text-dark',
      performance: 'bg-success',
    };
    const key = (category || 'general').toLowerCase();
    return categoryStyles[key] || 'bg-secondary';
  };

  return (
    <div className="announcements-editor">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Announcements</h4>
          <p className="text-muted mb-0">
            Create and manage announcements that appear on the dashboard for all users.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          + New Announcement
        </button>
      </div>

      {message && (
        <div
          className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible`}
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage(null)}
          ></button>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">
              {editingId ? 'Edit Announcement' : 'New Announcement'}
            </h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label" htmlFor="announcementTitle">Title</label>
                <input
                  type="text"
                  className="form-control"
                  id="announcementTitle"
                  name="title"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                  placeholder="e.g. Investment Committee Update - November"
                />
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label" htmlFor="announcementCategory">Category</label>
                  <select
                    className="form-select"
                    id="announcementCategory"
                    name="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="general">General</option>
                    <option value="update">Update</option>
                    <option value="alert">Alert</option>
                    <option value="performance">Performance</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">&nbsp;</label>
                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="is_pinned"
                      checked={formData.is_pinned}
                      onChange={(e) =>
                        setFormData({ ...formData, is_pinned: e.target.checked })
                      }
                    />
                    <label className="form-check-label" htmlFor="is_pinned">
                      Pin to top of announcements
                    </label>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label mb-0">Content</label>
                  <div className="d-flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                      id="image-upload-input"
                    />
                    <label
                      htmlFor="image-upload-input"
                      className="btn btn-sm btn-outline-secondary"
                      style={{ cursor: 'pointer' }}
                    >
                      {isUploading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1"></span>
                          Uploading...
                        </>
                      ) : (
                        '📷 Add Image'
                      )}
                    </label>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoSelect}
                      style={{ display: 'none' }}
                      id="video-upload-input"
                    />
                    <label
                      htmlFor="video-upload-input"
                      className="btn btn-sm btn-outline-secondary"
                      style={{ cursor: 'pointer' }}
                    >
                      {isUploadingVideo ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1"></span>
                          Uploading...
                        </>
                      ) : (
                        '🎬 Add Video'
                      )}
                    </label>
                  </div>
                </div>
                <textarea
                  ref={textareaRef}
                  className="form-control"
                  id="announcementContent"
                  name="content"
                  rows={8}
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  onPaste={handlePaste}
                  required
                  placeholder="Write your announcement content here... You can paste images directly (Ctrl+V)"
                />
                <small className="text-muted">
                  Supports line breaks, images, and videos. Paste images directly or use the buttons to upload.
                </small>
              </div>

              {/* Document Attachment */}
              <div className="mb-3">
                <label className="form-label">Document Attachment (Optional)</label>
                {formData.document_url ? (
                  <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                    <span className="text-primary">📄</span>
                    <span className="flex-grow-1">{formData.document_name}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleRemoveDocument}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                      onChange={handleDocumentSelect}
                      style={{ display: 'none' }}
                      id="document-upload-input"
                    />
                    <label
                      htmlFor="document-upload-input"
                      className="btn btn-outline-secondary"
                      style={{ cursor: 'pointer' }}
                    >
                      {isUploadingDocument ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1"></span>
                          Uploading...
                        </>
                      ) : (
                        '📄 Attach Document'
                      )}
                    </label>
                    <small className="text-muted ms-2">
                      PDF, Word, Excel, PowerPoint (max 50MB)
                    </small>
                  </div>
                )}
              </div>

              {/* Brand Visibility */}
              <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <p className="text-muted small mb-2">
                  Choose who can see this announcement on the dashboard.
                </p>
                <BrandVisibilityControl
                  keyPrefix="ann"
                  entityNoun="this announcement"
                  mode={formData.visibility_mode || (formData.for_all_brands === false ? 'include' : 'all')}
                  onModeChange={(newMode) => setFormData({
                    ...formData,
                    visibility_mode: newMode,
                    for_all_brands: newMode === 'all',
                    target_domains: newMode === 'all' ? [] : formData.target_domains,
                  })}
                  availableBrands={availableBrands}
                  selectedDomains={formData.target_domains}
                  onToggle={handleBrandToggle}
                />
              </div>

              {/* Email Delivery */}
              {emailLists.length > 0 && (
                <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                  <label className="form-label fw-bold mb-1">
                    <i className="bi bi-envelope me-1"></i>
                    Email Delivery
                  </label>
                  <p className="text-muted small mb-2">
                    Select lists to email when this announcement is created. Leave empty for no email.
                  </p>
                  <div className="row">
                    {emailLists.map((list) => (
                      <div className="col-md-6 col-lg-4" key={list.id}>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`email-list-${list.id}`}
                            checked={formData.email_list_ids.includes(list.id)}
                            onChange={() => handleEmailListToggle(list.id)}
                          />
                          <label className="form-check-label" htmlFor={`email-list-${list.id}`}>
                            {list.name}
                            <span className="badge bg-info ms-1" style={{ fontSize: '0.7em' }}>
                              {list.effective_member_count}
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : editingId ? (
                    'Update Announcement'
                  ) : (
                    'Create Announcement'
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Announcements List */}
      <div className="card">
        <div className="card-body p-0">
          {isLoading && !showForm ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm"></div>
            </div>
          ) : announcements.length === 0 ? (
            <p className="text-muted text-center py-4 mb-0">
              No announcements yet. Create your first one!
            </p>
          ) : (
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Title</th>
                  <th style={{ width: '100px' }}>Category</th>
                  <th style={{ width: '100px' }}>Brands</th>
                  <th style={{ width: '120px' }}>Author</th>
                  <th style={{ width: '150px' }}>Created</th>
                  <th style={{ width: '80px' }}>Status</th>
                  <th style={{ width: '200px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((announcement) => (
                  <tr
                    key={announcement.id}
                    className={!announcement.is_active ? 'table-secondary' : ''}
                  >
                    <td className="text-center">
                      {announcement.is_pinned && (
                        <span title="Pinned" style={{ color: 'var(--secondary-color)' }}>
                          📌
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="fw-semibold">
                        {announcement.title}
                        {announcement.document_url && (
                          <span className="ms-2 text-primary" title={`Attachment: ${announcement.document_name}`}>
                            📄
                          </span>
                        )}
                      </div>
                      <small className="text-muted">
                        {announcement.content.substring(0, 80)}
                        {announcement.content.length > 80 ? '...' : ''}
                      </small>
                    </td>
                    <td>
                      <span
                        className={`badge ${getCategoryBadge(announcement.category)}`}
                      >
                        {announcement.category}
                      </span>
                      {announcement.send_email && (
                        <i className="bi bi-envelope-fill ms-1 text-primary" title="Email notification sent" style={{ fontSize: '0.75rem' }}></i>
                      )}
                    </td>
                    <td>
                      {announcement.visibility_mode === 'exclude' ? (
                        <span className="badge bg-info text-dark" title={`Hidden from: ${announcement.target_domains?.join(', ')}`}>
                          🚫 {announcement.target_domains?.length || 0} hidden
                        </span>
                      ) : announcement.for_all_brands ? (
                        <span className="badge bg-success">All</span>
                      ) : (
                        <span className="badge bg-warning text-dark" title={announcement.target_domains?.join(', ')}>
                          {announcement.target_domains?.length || 0} brands
                        </span>
                      )}
                      {announcement.email_list_ids?.length > 0 && (
                        <span className="badge bg-info ms-1" title={announcement.email_list_names?.join(', ')}>
                          <i className="bi bi-envelope-fill" style={{fontSize:'0.65rem'}}></i> {announcement.email_list_ids.length}
                        </span>
                      )}
                    </td>
                    <td>{announcement.author_name}</td>
                    <td>
                      <small>{formatDate(announcement.created_at)}</small>
                    </td>
                    <td>
                      {announcement.is_active ? (
                        <span className="badge bg-success">Visible</span>
                      ) : (
                        <span className="badge bg-danger">Hidden</span>
                      )}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => handleEdit(announcement)}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          className={`btn ${announcement.is_pinned ? 'btn-secondary' : 'btn-outline-secondary'}`}
                          onClick={() => handleTogglePinned(announcement)}
                          title={announcement.is_pinned ? 'Unpin' : 'Pin'}
                        >
                          📌
                        </button>
                        <button
                          className={`btn ${announcement.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                          onClick={() => handleToggleActive(announcement)}
                          title={announcement.is_active ? 'Hide' : 'Show'}
                        >
                          {announcement.is_active ? 'Hide' : 'Show'}
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDelete(announcement)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="row mt-4">
        <div className="col-md-4">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3 className="mb-0">{announcements.length}</h3>
              <small className="text-muted">Total Announcements</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3 className="mb-0 text-success">
                {announcements.filter((a) => a.is_active).length}
              </h3>
              <small className="text-muted">Visible</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3 className="mb-0" style={{ color: 'var(--secondary-color)' }}>
                {announcements.filter((a) => a.is_pinned).length}
              </h3>
              <small className="text-muted">Pinned</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementsEditor;
